import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Lock, Users, CheckCircle, Share2, Image as ImageIcon, Ticket, AlertCircle,
  RefreshCw, Plus, LogOut, BarChart3, ListChecks, Sparkles, ExternalLink, Wallet, Webhook,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "sessions", label: "Hasil", icon: ListChecks },
  { key: "purchases", label: "Transaksi", icon: Wallet },
  { key: "webhook", label: "Webhook Log", icon: Webhook },
  { key: "questions", label: "Pertanyaan", icon: ListChecks },
  { key: "characters", label: "Karakter", icon: Sparkles },
  { key: "tokens", label: "Token", icon: Ticket },
  { key: "errors", label: "Error Log", icon: AlertCircle },
];

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    const t = localStorage.getItem("rk_admin_token");
    if (t) api.defaults.headers.common.Authorization = `Bearer ${t}`;
    api.get("/admin/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem("rk_admin_token", data.token);
      api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
      setAuthed(true);
    } catch (e2) { setErr(formatErr(e2.response?.data?.detail)); }
  };

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch (e) { console.error("admin logout failed", e); }
    localStorage.removeItem("rk_admin_token");
    delete api.defaults.headers.common.Authorization;
    setAuthed(false);
  };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full border-2 border-amber-400/30 border-t-amber-300 animate-spin" /></div>;

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        <motion.form onSubmit={login} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm glass rounded-3xl p-8" data-testid="admin-login-form">
          <div className="w-12 h-12 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center mb-6">
            <Lock size={20} className="text-amber-300" />
          </div>
          <h1 className="font-display text-3xl gold-text mb-1">Admin Ruang Kendali</h1>
          <p className="text-sm text-gray-400 mb-6">Masuk buat lihat dashboard.</p>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email-input"
            className="w-full mb-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-amber-400/50" />
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Password" data-testid="admin-password-input"
            className="w-full mb-4 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-amber-400/50" />
          {err && <p className="text-sm text-red-400 mb-4" data-testid="admin-login-error">{err}</p>}
          <button type="submit" data-testid="admin-login-button" className="w-full gold-btn rounded-xl py-3">Masuk</button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full">
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="font-display text-2xl tracking-[0.12em]">RUANG <span className="gold-text">KENDALI</span> · Admin</div>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-amber-200 transition-colors"><LogOut size={16} /> Keluar</button>
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors ${
                tab === t.key ? "bg-amber-400/15 border-amber-400/50 text-amber-200" : "border-white/10 text-gray-400 hover:text-gray-200"
              }`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview />}
        {tab === "sessions" && <Sessions />}
        {tab === "purchases" && <Purchases />}
        {tab === "webhook" && <WebhookLogs />}
        {tab === "questions" && <Questions />}
        {tab === "characters" && <Characters />}
        {tab === "tokens" && <Tokens />}
        {tab === "errors" && <Errors />}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color = "#E5C158" }) => (
  <div className="card-surface rounded-2xl p-5">
    <div className="flex items-center gap-2 text-gray-400 text-xs mb-2"><Icon size={15} style={{ color }} /> {label}</div>
    <div className="font-display text-3xl gold-text">{value}</div>
  </div>
);

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <Loading />;
  const funnelMax = Math.max(1, ...Object.values(s.funnel));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total sesi" value={s.sessions_total} />
        <Stat icon={CheckCircle} label="Selesai analisis" value={s.completed} color="#10B981" />
        <Stat icon={Share2} label="Total share" value={s.share_count} color="#EC4899" />
        <Stat icon={ImageIcon} label="Gambar pending/gagal" value={s.image_pending} color="#F59E0B" />
      </div>

      <div className="card-surface rounded-2xl p-6">
        <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-amber-300/80 mb-5">Conversion funnel</h3>
        <div className="space-y-3">
          {Object.entries(s.funnel).map(([k, v]) => (
            <div key={k}>
              <div className="flex justify-between text-xs text-gray-400 mb-1"><span>{k}</span><span className="font-mono">{v}</span></div>
              <div className="h-2.5 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-300 to-amber-500" style={{ width: `${(v / funnelMax) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-surface rounded-2xl p-6">
          <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-amber-300/80 mb-4">Karakter terpopuler</h3>
          {s.top_characters.length === 0 && <p className="text-sm text-gray-500">Belum ada data.</p>}
          {s.top_characters.map((c) => (
            <div key={c.name} className="flex justify-between py-2 border-b border-white/5 text-sm">
              <span className="text-gray-300">{c.name}</span><span className="font-mono text-amber-300">{c.count}</span>
            </div>
          ))}
        </div>
        <div className="card-surface rounded-2xl p-6">
          <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-amber-300/80 mb-4">Per kategori</h3>
          {s.by_category.length === 0 && <p className="text-sm text-gray-500">Belum ada data.</p>}
          {s.by_category.map((c) => (
            <div key={c.category} className="flex justify-between py-2 border-b border-white/5 text-sm">
              <span className="text-gray-300 capitalize">{c.category}</span><span className="font-mono text-amber-300">{c.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Sessions() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/sessions").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const regen = async (pid) => {
    try { await api.post(`/admin/results/${pid}/regenerate-image`); toast.success("Regenerate dimulai"); setTimeout(load, 1000); }
    catch { toast.error("Gagal"); }
  };
  if (!rows) return <Loading />;
  return (
    <div className="card-surface rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-gray-500 border-b border-white/10">
          <th className="p-4">Hasil</th><th className="p-4">Kategori</th><th className="p-4">Karakter</th><th className="p-4">Match</th><th className="p-4">Gambar</th><th className="p-4"></th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.public_id} className="border-b border-white/5">
              <td className="p-4 font-mono text-amber-300/80">{r.public_id}</td>
              <td className="p-4 capitalize text-gray-300">{r.category}</td>
              <td className="p-4 text-gray-200">{r.character_name}</td>
              <td className="p-4 font-mono">{r.compatibility}%</td>
              <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${r.image_status === "done" ? "bg-emerald-500/15 text-emerald-300" : r.image_status === "failed" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>{r.image_status}</span></td>
              <td className="p-4 flex items-center gap-3">
                <button onClick={() => regen(r.public_id)} data-testid={`regen-${r.public_id}`} className="text-gray-400 hover:text-amber-200"><RefreshCw size={15} /></button>
                <a href={`/hasil/${r.public_id}`} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-amber-200"><ExternalLink size={15} /></a>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-500">Belum ada hasil.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Questions() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/questions").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (q) => {
    await api.put(`/admin/questions/${q.key}`, { active: !q.active });
    load();
  };
  const editText = async (q) => {
    const t = window.prompt("Edit pertanyaan:", q.question);
    if (t && t !== q.question) { await api.put(`/admin/questions/${q.key}`, { question: t }); load(); toast.success("Tersimpan"); }
  };
  if (!rows) return <Loading />;
  return (
    <div className="card-surface rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-gray-500 border-b border-white/10">
          <th className="p-4">Kategori</th><th className="p-4">Pertanyaan</th><th className="p-4">Tipe</th><th className="p-4">Aktif</th>
        </tr></thead>
        <tbody>
          {rows.map((q) => (
            <tr key={q.key} className="border-b border-white/5">
              <td className="p-4 capitalize text-gray-400">{q.category}</td>
              <td className="p-4 text-gray-200 max-w-md">
                <button onClick={() => editText(q)} data-testid={`edit-q-${q.key}`} className="text-left hover:text-amber-200">{q.question}</button>
              </td>
              <td className="p-4 text-gray-400">{q.question_type}</td>
              <td className="p-4">
                <button onClick={() => toggle(q)} data-testid={`toggle-q-${q.key}`}
                  className={`w-11 h-6 rounded-full transition-colors relative ${q.active ? "bg-amber-400" : "bg-white/15"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-slate-900 transition-all ${q.active ? "left-5" : "left-0.5"}`} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Characters() {
  const [rows, setRows] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ name: "", icon: "✨", category_key: "diri", tagline: "",
    description: "", advice: "", visual_prompt: "", rarity: "common", profile: "", unlock: "" });
  const load = useCallback(() => api.get("/admin/characters").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const editTagline = async (c) => {
    const t = window.prompt(`Tagline ${c.name}:`, c.tagline);
    if (t && t !== c.tagline) { await api.put(`/admin/characters/${c.key}`, { tagline: t }); load(); toast.success("Tersimpan"); }
  };

  const submit = async () => {
    let profile = {}, unlock = null;
    try { profile = f.profile ? JSON.parse(f.profile) : {}; }
    catch { toast.error('Profile harus JSON valid, mis. {"emo":80,"res":60}'); return; }
    try { unlock = f.unlock ? JSON.parse(f.unlock) : null; }
    catch { toast.error('Unlock harus JSON valid, mis. {"emo":{"min":80}}'); return; }
    if (!f.name) { toast.error("Nama wajib"); return; }
    try {
      await api.post("/admin/characters", {
        name: f.name, icon: f.icon, category_key: f.category_key, categories: [f.category_key],
        tagline: f.tagline, description: f.description, advice: f.advice,
        visual_prompt: f.visual_prompt, rarity: f.rarity, profile, unlock,
      });
      toast.success("Karakter ditambah 🎉");
      setShowForm(false);
      setF({ name: "", icon: "✨", category_key: "diri", tagline: "", description: "", advice: "", visual_prompt: "", rarity: "common", profile: "", unlock: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal nambah"); }
  };

  const inp = "w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-amber-400/50";
  if (!rows) return <Loading />;
  return (
    <div className="space-y-5">
      <button onClick={() => setShowForm(!showForm)} data-testid="toggle-char-form"
        className="gold-btn rounded-xl px-5 py-2.5 inline-flex items-center gap-2"><Plus size={16} /> Tambah Karakter</button>

      {showForm && (
        <div className="card-surface rounded-2xl p-5 space-y-3" data-testid="char-create-form">
          <div className="grid grid-cols-2 gap-3">
            <input className={inp} placeholder="Nama" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cf-name" />
            <input className={inp} placeholder="Icon/emoji" value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} data-testid="cf-icon" />
            <select className={inp} value={f.category_key} onChange={(e) => setF({ ...f, category_key: e.target.value })} data-testid="cf-category">
              {["kerja", "uang", "relasi", "diri", "keputusan"].map((c) => <option key={c} value={c} className="bg-slate-900">{c}</option>)}
            </select>
            <select className={inp} value={f.rarity} onChange={(e) => setF({ ...f, rarity: e.target.value })} data-testid="cf-rarity">
              <option value="common" className="bg-slate-900">common</option>
              <option value="rare" className="bg-slate-900">rare</option>
            </select>
          </div>
          <input className={inp} placeholder="Tagline" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} data-testid="cf-tagline" />
          <textarea className={inp} rows={2} placeholder="Deskripsi" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="cf-desc" />
          <textarea className={inp} rows={2} placeholder="Advice / Ruang Kendali" value={f.advice} onChange={(e) => setF({ ...f, advice: e.target.value })} />
          <textarea className={inp} rows={2} placeholder="Visual prompt (buat generate gambar)" value={f.visual_prompt} onChange={(e) => setF({ ...f, visual_prompt: e.target.value })} />
          <textarea className={inp} rows={2} placeholder='Profile JSON, mis. {"emo":80,"res":60,"bound":40}' value={f.profile} onChange={(e) => setF({ ...f, profile: e.target.value })} data-testid="cf-profile" />
          {f.rarity === "rare" && (
            <textarea className={inp} rows={2} placeholder='Aturan langka JSON, mis. {"emo":{"min":80},"dec":{"max":35}}' value={f.unlock} onChange={(e) => setF({ ...f, unlock: e.target.value })} data-testid="cf-unlock" />
          )}
          <button onClick={submit} data-testid="cf-submit" className="gold-btn rounded-xl px-5 py-2.5">Simpan Karakter</button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {rows.map((c) => (
          <div key={c.key} className="card-surface rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-2"><span className="text-3xl">{c.icon}</span>
              <span className="font-display text-xl gold-text">{c.name}</span>
              {c.rarity === "rare" && (
                <span className="text-[10px] font-semibold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full border border-amber-300/50 bg-amber-400/10 text-amber-200">Langka</span>
              )}
              <span className="ml-auto text-[10px] text-gray-500 uppercase">{(c.categories || [])[0]}</span>
            </div>
            <button onClick={() => editTagline(c)} data-testid={`edit-char-${c.key}`} className="text-sm text-gray-400 italic text-left hover:text-amber-200">"{c.tagline}"</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tokens() {
  const [rows, setRows] = useState(null);
  const [count, setCount] = useState(1);
  const load = useCallback(() => api.get("/admin/tokens").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    const { data } = await api.post("/admin/tokens", { count: Number(count) || 1 });
    toast.success(`${data.tokens.length} token dibuat`);
    load();
  };
  if (!rows) return <Loading />;
  return (
    <div className="space-y-5">
      <div className="card-surface rounded-2xl p-5 flex items-center gap-3">
        <input type="number" min={1} max={100} value={count} onChange={(e) => setCount(e.target.value)} data-testid="token-count-input"
          className="w-24 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-amber-400/50" />
        <button onClick={create} data-testid="create-token-button" className="gold-btn rounded-xl px-5 py-2.5 inline-flex items-center gap-2"><Plus size={16} /> Buat token manual</button>
        <span className="text-xs text-gray-500 ml-auto">Mode akses saat ini: <b className="text-amber-300">open</b></span>
      </div>
      <div className="card-surface rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-500 border-b border-white/10"><th className="p-4">Token</th><th className="p-4">Status</th><th className="p-4">Dibuat</th></tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.token} className="border-b border-white/5">
                <td className="p-4 font-mono text-amber-300/80 break-all">{t.token}</td>
                <td className="p-4">{t.status}</td>
                <td className="p-4 text-gray-500">{(t.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-gray-500">Belum ada token.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Errors() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/errors").then((r) => setRows(r.data)).catch(() => {}); }, []);
  if (!rows) return <Loading />;
  return (
    <div className="card-surface rounded-2xl p-6 space-y-3">
      {rows.length === 0 && <p className="text-sm text-gray-500">Nggak ada error. Mantap 🎉</p>}
      {rows.map((e) => (
        <div key={e.id} className="border-b border-white/5 pb-3">
          <div className="flex justify-between text-xs"><span className="text-red-400">{e.source}</span><span className="text-gray-600 font-mono">{(e.timestamp || "").slice(0, 19)}</span></div>
          <p className="text-sm text-gray-300 mt-1">{e.message}</p>
        </div>
      ))}
    </div>
  );
}

function Purchases() {
  const [rows, setRows] = useState(null);
  const [actEmail, setActEmail] = useState("");
  const [actBusy, setActBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/purchases").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const confirm = async (p) => {
    const ref = window.prompt("Ref transaksi Lynk (opsional):", "");
    if (ref === null) return;
    try {
      await api.post("/admin/purchases/confirm", { purchase_id: p.id, lynk_ref: ref || undefined });
      toast.success("Transaksi dikonfirmasi ✓");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const activateByEmail = async () => {
    if (!actEmail.trim()) { toast.error("Isi email dulu"); return; }
    setActBusy(true);
    try {
      const { data } = await api.post("/admin/purchases/activate-email", { email: actEmail.trim() });
      toast.success(data.already_paid ? "Akun ini memang sudah aktif" : `Akses ${data.activated} diaktifkan ✓`);
      setActEmail("");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setActBusy(false); }
  };
  if (!rows) return <Loading />;
  return (
    <div className="space-y-5">
      <div className="card-surface rounded-2xl p-5" data-testid="activate-email-form">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-amber-300/80 mb-3">Aktivasi by Email</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input value={actEmail} onChange={(e) => setActEmail(e.target.value)}
            placeholder="email pembeli (lihat di dashboard Lynk)" data-testid="activate-email-input"
            className="flex-1 rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-amber-400/50" />
          <button onClick={activateByEmail} disabled={actBusy} data-testid="activate-email-button"
            className="gold-btn rounded-xl px-5 py-2.5 disabled:opacity-60">
            {actBusy ? "Memproses…" : "Aktifkan Akses"}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">Buat pembeli yang webhook-nya terlanjur gagal — akun langsung aktif, ditandai manual-email.</p>
      </div>
    <div className="card-surface rounded-2xl overflow-hidden">
      <table className="w-full text-sm" data-testid="purchases-table">
        <thead><tr className="text-left text-gray-500 border-b border-white/10">
          <th className="p-4">Email</th><th className="p-4">Kode klaim</th><th className="p-4">Status</th>
          <th className="p-4">Nominal</th><th className="p-4">Ref Lynk</th><th className="p-4">Dibuat</th><th className="p-4"></th>
        </tr></thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-b border-white/5">
              <td className="p-4 text-gray-200">{p.email}</td>
              <td className="p-4 font-mono text-amber-300/80">{p.claim_code}</td>
              <td className="p-4">
                <span className={`text-xs px-2 py-1 rounded-full ${p.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}
                  data-testid={`purchase-status-${p.id}`}>
                  {p.status}{p.status === "paid" ? (p.verified ? " ✓verified" : " · unverified") : ""}
                </span>
              </td>
              <td className="p-4 font-mono">Rp{(p.expected_amount || 0).toLocaleString("id-ID")}</td>
              <td className="p-4 text-gray-500 font-mono text-xs">{p.lynk_ref || "—"}</td>
              <td className="p-4 text-gray-500">{(p.created_at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="p-4">
                {(p.status === "pending" || p.status === "amount_mismatch") && (
                  <button onClick={() => confirm(p)} data-testid={`confirm-purchase-${p.id}`}
                    className="text-xs gold-btn rounded-lg px-3 py-1.5">Konfirmasi</button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-gray-500">Belum ada transaksi.</td></tr>}
        </tbody>
      </table>
    </div>
    </div>
  );
}

function WebhookLogs() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/webhook-logs").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  const actionColor = (a) => a === "paid" ? "bg-emerald-500/15 text-emerald-300"
    : a === "ignored" || a === "ping" ? "bg-white/10 text-gray-400"
    : "bg-red-500/15 text-red-300";
  return (
    <div className="space-y-3" data-testid="webhook-logs">
      <div className="flex justify-end">
        <button onClick={load} data-testid="refresh-webhook-logs" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-amber-200 transition-colors"><RefreshCw size={14} /> Refresh</button>
      </div>
      {rows.length === 0 && <div className="card-surface rounded-2xl p-8 text-center text-gray-500">Belum ada webhook masuk.</div>}
      {rows.map((w) => (
        <div key={w.id} className="card-surface rounded-2xl p-4">
          <button onClick={() => setOpen(open === w.id ? null : w.id)} data-testid={`webhook-log-${w.id}`}
            className="w-full flex flex-wrap items-center gap-3 text-left">
            <span className={`text-xs px-2 py-1 rounded-full ${actionColor(w.action)}`}>{w.action}</span>
            <span className="text-xs text-gray-500 font-mono">{(w.timestamp || "").slice(0, 19).replace("T", " ")}</span>
            <span className="text-sm text-gray-300">{w.event || "—"}</span>
            <span className="text-xs text-gray-500 ml-auto">
              sig: <b className={w.sig === "valid" ? "text-emerald-300" : "text-amber-300"}>{w.sig}</b>
              {w.ref ? ` · ref ${w.ref}` : ""}{w.email ? ` · ${w.email}` : ""}{w.amount != null ? ` · ${w.amount}` : ""}
            </span>
          </button>
          {open === w.id && (
            <pre className="mt-3 p-3 rounded-xl bg-black/40 text-xs text-gray-400 overflow-x-auto whitespace-pre-wrap break-all"
              data-testid={`webhook-log-body-${w.id}`}>{w.body}</pre>
          )}
        </div>
      ))}
    </div>
  );
}

const Loading = () => <div className="py-16 flex justify-center"><div className="w-10 h-10 rounded-full border-2 border-amber-400/30 border-t-amber-300 animate-spin" /></div>;
